import { useT } from "../../../shared/i18n/i18n";
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight";
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { common, createLowlight } from "lowlight";

// Code block with a language picker in the corner (the official tiptap
// node-view pattern): lowlight highlights from node.attrs.language, "auto"
// falls back to detection.

export const lowlight = createLowlight(common);
const languages = [...lowlight.listLanguages()].sort();

function CodeBlockView({ editor, node, updateAttributes }: NodeViewProps) {
  const t = useT();
  const language = typeof node.attrs.language === "string" ? node.attrs.language : "";

  return (
    <NodeViewWrapper className="document-code-block">
      {editor.isEditable ? (
        <select
          aria-label={t("editor.codeLanguage")}
          className="document-code-block__language"
          contentEditable={false}
          onChange={(event) => updateAttributes({ language: event.target.value || null })}
          value={languages.includes(language) ? language : ""}
        >
          <option value="">auto</option>
          {languages.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
      ) : language ? (
        <span className="document-code-block__language" contentEditable={false}>
          {language}
        </span>
      ) : null}
      <pre>
        {/* The prop type only names "div", but any tag is supported at runtime. */}
        <NodeViewContent as={"code" as unknown as "div"} />
      </pre>
    </NodeViewWrapper>
  );
}

export const DocumentCodeBlock = CodeBlockLowlight.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      // Mirror the language onto the rendered <pre> so exports carry it.
      language: {
        default: null,
        parseHTML: (element) => element.firstElementChild?.getAttribute("class")?.replace("language-", "") ?? null,
        renderHTML: (attributes) => (attributes.language ? { "data-language": attributes.language } : {}),
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(CodeBlockView);
  },
});
